import React from 'react'
import styled from 'styled-components'
import { Modal } from '../Modal'
import { Colors, FontWeights, INTEGRAL_SYMBOL } from '../../constants'
import { formatCurrency } from '../../model'
import { useApiPools } from '../../providers/apiPools/useApiPools'
import { TokenRewards } from '../../providers/apiTokenRewards/model'
import { bigNumber18DecimalsToPercent } from '../../utils'
import { BigNumber } from 'ethers'
import { TradeRewardsProgressBar } from './TradeRewardsProgressBar'

interface Props {
  className?: string
  apiTokenRewards?: TokenRewards
  onClose: () => void
}

export function TokenRewardsModal({ className, apiTokenRewards, onClose }: Props) {
  const apiPools = useApiPools()

  const lpRewardsTotal = apiTokenRewards
    ? `${formatCurrency(
        18,
        Object.keys(apiTokenRewards.lpRewards).reduce((memo, address) => {
          const lpRewards = apiTokenRewards.lpRewards[address]
          return memo.add(lpRewards.tokenRewards.total)
        }, BigNumber.from(0))
      )} ${INTEGRAL_SYMBOL}`
    : '-'

  const lpRewardsTable =
    apiTokenRewards &&
    Object.keys(apiTokenRewards.lpRewards).map((address) => {
      const lpRewards = apiTokenRewards.lpRewards[address]

      let aprDisplay
      const apiPoolData = apiPools?.[address]
      if (apiPoolData) {
        const { apr, potentialApr } = apiPoolData
        if (!apr.eq(potentialApr)) {
          aprDisplay = (
            <APR warning={true}>
              {bigNumber18DecimalsToPercent(apr)}
              <small>Deposit to restore {bigNumber18DecimalsToPercent(potentialApr)} APR</small>
            </APR>
          )
        } else {
          aprDisplay = <APR>{bigNumber18DecimalsToPercent(apr)}</APR>
        }
      }

      return (
        <tr key={lpRewards.pairName}>
          <td>{lpRewards.pairName}</td>
          <td>{formatCurrency(18, lpRewards.tokenRewards.total)}</td>
          <td>{aprDisplay}</td>
        </tr>
      )
    })

  const tradeRewardsTotal = apiTokenRewards
    ? `${formatCurrency(18, apiTokenRewards.tradeRewards.tokenRewards.total)} ${INTEGRAL_SYMBOL}`
    : '-'

  return (
    <Modal title="Accrued Rewards" onClose={onClose}>
      <Content className={className}>
        <Header>
          <span>Trade Rewards</span>
          <Total>
            <strong>Total</strong>
            {tradeRewardsTotal}
          </Total>
        </Header>
        <TradeRewardsProgressBar apiTokenRewards={apiTokenRewards} />
        <Hr />
        <Header>
          <span>LP Rewards</span>
          <Total>
            <strong>Total</strong>
            {lpRewardsTotal}
          </Total>
        </Header>
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Pool</th>
                <th>LP Rewards ({INTEGRAL_SYMBOL})</th>
                <th>
                  APR<sup>*</sup>
                </th>
              </tr>
            </thead>
            <tbody>{lpRewardsTable}</tbody>
          </Table>
        </TableContainer>
      </Content>
    </Modal>
  )
}

const Content = styled.div`
  padding: 10px 15px;
  color: ${Colors.Black};
  font-weight: ${FontWeights.Regular};
`

const Header = styled.h1`
  margin: 10px 5px;
  padding: 0;
  display: flex;
  justify-content: space-between;
  font-size: 15px;
  font-weight: ${FontWeights.Semibold};
  text-transform: uppercase;
`

const Total = styled.span`
  font-size: 11px;
  font-weight: ${FontWeights.Regular};
  color: ${Colors.DarkerGray};

  strong {
    margin-right: 10px;
    font-weight: ${FontWeights.Semibold};
  }
`

const Hr = styled.hr`
  padding: 0;
  margin: 15px 5px;
  height: 1px;
  background-color: ${Colors.LightererGray};
  border: 0;
`

const TableContainer = styled.div`
  overflow-x: auto;
`

const Table = styled.table`
  width: 100%;
  font-size: 12px;

  th,
  td {
    padding: 3px 5px;
    text-align: left;
    text-transform: uppercase;

    &:first-child {
      white-space: nowrap;
    }

    &:last-child {
      text-align: right;
    }
  }

  th {
    font-size: 11px;
    vertical-align: bottom;
    white-space: nowrap;
  }

  td {
    color: ${Colors.DarkerGray};
  }
`

const APR = styled.span<{ warning?: boolean }>`
  display: block;
  color: ${({ warning }) => (warning ? Colors.DarkRed : Colors.DarkerGray)};

  small {
    display: block;
    font-size: 10px;
    text-transform: none;
  }
`
